import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { allConnectorsLink } from '@app/constants';
import { MyAppsStore } from '@app/store/myapps.store';
import { Connectors } from '@interfaces/connector';
import { ConnectorsService } from '@services/connectors.service';
import { DeviceTypeService } from '@services/device-type.service';
import { UserService } from '@services/user.service';
import { UtilsService } from '@services/utils.service';
import { NgxMasonryComponent } from 'ngx-masonry';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-my-apps',
  templateUrl: './my-apps.component.html',
  styleUrls: ['./my-apps.component.scss'],
  standalone: false,
})
export class MyAppsComponent implements OnInit, OnDestroy {
  public readonly myAppsStore: InstanceType<typeof MyAppsStore> = inject(MyAppsStore);

  public loading = false;
  public connectorFilter = '';
  public isListView = false;
  private readonly minWithCard = 358.333;
  public readonly gutter = 16;
  public masonryCardWidth = this.minWithCard;
  public searchVisible: boolean = false;
  public connectorFilterMenu: { name: string; label: string; count: number; checked: boolean }[] = [
    { name: 'all', label: 'all', count: 0, checked: true },
    { name: 'active', label: 'active', count: 0, checked: false },
    { name: 'inactive', label: 'inactive', count: 0, checked: false },
    { name: 'pending', label: 'pending_connection', count: 0, checked: false },
  ];
  public connectorTypeFilter: string = 'all';

  public mansoryOptions = {
    gutter: this.gutter,
    fitWidth: true,
    originTop: true,
    originLeft: true,
  };

  public placeholderItems: number = 1;
  @ViewChild('placeholderWrapper') placeholderWrapper!: ElementRef;
  @ViewChild('placeHolderItem') placeHolderItem!: ElementRef;
  @ViewChild('connectedMasonryContainer') connectedMasonry!: NgxMasonryComponent;
  @ViewChild('connectedMasonryContainer', { read: ElementRef }) connectedMasonryElementRef!: ElementRef;
  @ViewChild('notConnectedMasonryContainer') notConnectedMasonry!: NgxMasonryComponent;
  @ViewChild('notConnectedMasonryContainer', { read: ElementRef }) notConnectedMasonryElementRef!: ElementRef;
  @ViewChildren('connectorCardConnected') connectorCardConnected!: QueryList<ElementRef>;
  @ViewChildren('connectorCardDisconnected') connectorCardDisconnected!: QueryList<ElementRef>;

  @HostListener('window:resize', ['$event']) onResize(event: any) {
    setTimeout(() => {
      this.resizeConnected();
    }, 200);
  }

  constructor(
    private connectorsService: ConnectorsService,
    public userService: UserService,
    public deviceType: DeviceTypeService,
    public utilService: UtilsService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    const lastUpdated = this.myAppsStore.lastUpdated();
    const isStale = !lastUpdated || Date.now() - lastUpdated > this.myAppsStore.CACHE_DURATION_MS();

    if (this.myAppsStore.isInitialLoad() || isStale) {
      if (this.myAppsStore.isInitialLoad()) {
        this.spinner.show();
        this.loading = true;
      }
      this.myAppsStore.loadActiveConnectors();
    }
  }

  refreshRepeating(refreshType?: 'initial' | 'manual' | 'resize'): void {
    const time = refreshType === 'initial' ? 2000 : 500;
    setTimeout(() => {
      this.resizeConnected();
    }, time);
  }

  ngAfterViewInit() {
    const offsetHeight = this.placeholderWrapper?.nativeElement?.offsetHeight;
    const itemOffsetHeight = this.placeHolderItem?.nativeElement?.offsetHeight;
    this.placeholderItems = Math.floor(offsetHeight / itemOffsetHeight);
    this.cdr.detectChanges();
    let connectedSubscription = this.connectorCardConnected.changes.subscribe(() => {
      if (this.connectorCardConnected.length === this.myAppsStore.connectedFilteredConnectors().length) {
        setTimeout(() => {
          this.loading = false;
          connectedSubscription.unsubscribe();
          this.resizeConnected();
        }, 1000);
      }
    });
    let disconnectedSubscription = this.connectorCardDisconnected.changes.subscribe(() => {
      if (this.connectorCardDisconnected.length === this.myAppsStore.disconnectedFilteredConnectors().length) {
        setTimeout(() => {
          this.loading = false;
          disconnectedSubscription.unsubscribe();
          this.resizeConnected();
        }, 1000);
      }
    });
  }

  public resizeConnected() {
    if (!this.connectedMasonryElementRef?.nativeElement || !this.notConnectedMasonryElementRef?.nativeElement) {
      return;
    }

    const connectedMasonryWidth = this.connectedMasonryElementRef.nativeElement.offsetWidth;
    const notConnectedMasonryWidth = this.notConnectedMasonryElementRef.nativeElement.offsetWidth;

    if (!connectedMasonryWidth || !notConnectedMasonryWidth) {
      return;
    }

    const masonryWidth = Math.min(connectedMasonryWidth, notConnectedMasonryWidth);
    const cardWithGutter = this.minWithCard + this.gutter;
    const cardsPerRow = Math.max(1, Math.floor(masonryWidth / cardWithGutter));

    const totalGutterSpace = this.gutter * (cardsPerRow - 1);
    const availableWidth = masonryWidth - totalGutterSpace;
    const newCardWidth = availableWidth / cardsPerRow;

    this.masonryCardWidth = newCardWidth;

    if (this.connectedMasonry) {
      this.connectedMasonry.reloadItems();
      this.connectedMasonry.layout();
    }
    if (this.notConnectedMasonry) {
      this.notConnectedMasonry.reloadItems();
      this.notConnectedMasonry.layout();
    }
  }

  /**
   * toggleConnectorFilter - Toggle the connector filter based on the option selected.
   * @param option
   * @returns void
   */
  public toggleConnectorFilter(option: string) {
    this.connectorFilterMenu.forEach((filter) => {
      filter.checked = filter.name === option;
    });
    this.myAppsStore.toggleConnectorFilter(option);
  }

  clearConnectorFilter(): void {
    this.connectorFilter = '';
    this.myAppsStore.setConnectorFilter('');
    this.myAppsStore.sortConnectorsByType(
      this.myAppsStore.connectedFilteredConnectors(),
      this.myAppsStore.disconnectedFilteredConnectors(),
    );
    if (!this.deviceType.isDesktop) {
      this.hideSearch();
    }
  }

  /**
   * Opens a new OAuth window with the specified URL and dimensions.
   * @param url - The URL to open in the OAuth window.
   * @param name - The name of the OAuth window (default: 'Authorization').
   * @param w - The width of the OAuth window (default: 500).
   * @param h - The height of the OAuth window (default: 600).
   * @returns The reference to the newly opened window or null if the URL is null.
   */
  private createOauthWindow(url: string, name = 'Authorization', w = 750, h = 600) {
    if (url == null) {
      return null;
    }

    const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
    const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

    const width = window.innerWidth
      ? window.innerWidth
      : document.documentElement.clientWidth
        ? document.documentElement.clientWidth
        : screen.width;
    const height = window.innerHeight
      ? window.innerHeight
      : document.documentElement.clientHeight
        ? document.documentElement.clientHeight
        : screen.height;

    const systemZoom = width / window.screen.availWidth;
    const left = (width - w) / 2 / systemZoom + dualScreenLeft;
    const top = (height - h) / 2 / systemZoom + dualScreenTop;

    const options = `width=${w / systemZoom},height=${h / systemZoom},left=${left},top=${top},popup=true`;
    return window.open(url, name, options);
  }

  /**
   * Performs authorization by creating an OAuth window.
   * @param url - The URL for authorization.
   * @param connectorName - The name of the connector.
   * @returns The window handle for the OAuth window.
   */
  private doAuthorization(url: string, connector: Connectors, type: 'login' | 'logout' = 'login') {
    const popup = this.createOauthWindow(url, `Authorization for ${connector.name}`);

    if (popup) {
      // Create a function to check if the popup has been closed
      const checkPopupClosed = setInterval(() => {
        if (popup.closed) {
          this.checkConnectorStatus(connector.uuid, type);
          clearInterval(checkPopupClosed);
        }
      }, 2000);

      setTimeout(() => {
        clearInterval(checkPopupClosed);
        this.refreshRepeating();
      }, 120000);
    }
  }

  toggleSwitch(connector: any) {
    const parentConnector = connector.parent
      ? this.myAppsStore.allConnectors().find((c) => c.uuid === connector.parent.uuid)
      : null;

    // Handle sub-connectors
    if (parentConnector) {
      if (parentConnector.is_enabled) {
        connector.is_enabled ? this.disableConnector(connector.uuid) : this.enableConnector(connector.uuid);
      } else {
        this.enableConnector(parentConnector.uuid);
      }
      return;
    }

    // Handle parent connectors
    if (connector.is_enabled) {
      this.disableConnector(connector.uuid);
    } else {
      this.enableConnector(connector.uuid);
    }
  }

  /**
   * Enables a connector with the given UUID.
   * @param connectorUuid The UUID of the connector to disable.
   * @returns void
   */
  public enableConnector(connectorUuid: string): void {
    this.myAppsStore.enableConnector(connectorUuid);
  }

  /**
   * Disables a connector by its UUID.
   * @param connectorUuid The UUID of the connector to disable.
   * @param connectorIndex The index of the connector in the connectors array.
   * @param isSubConnector Whether the connector is a subconnector.
   * @returns void
   */
  public disableConnector(connectorUuid: string): void {
    this.myAppsStore.disableConnector(connectorUuid);
  }

  /**
   * Navigates to the all connectors page.
   * @returns void
   */
  gotoAllConnectors(): void {
    window.open(allConnectorsLink, '_blank');
  }

  /**
   * Open new tab with connector login url
   * call checkConnectorForLoginStatus to check login status
   * @param connector - The connector to log in to.
   * @returns void
   */
  public gotoConnectorLogin(connector: Connectors): void {
    const user = this.userService.getUserData();
    const loginUrl = this.connectorsService.getAuthenticateConnector(
      connector.uuid,
      user?.uuid ?? '',
      user?.company?.uuid ?? '',
    );
    this.doAuthorization(loginUrl, connector, 'login');
  }

  /**
   * Logs out the specified connector and checks its login status.
   * @param connector - The connector to log out.
   * @returns void
   */
  public logoutConnector(connector: Connectors): void {
    const user = this.userService.getUserData();
    const logoutUrl = this.connectorsService.getUnAuthenticateConnector(
      connector.uuid,
      user?.uuid ?? '',
      user?.company?.uuid ?? '',
    );
    this.doAuthorization(logoutUrl, connector, 'logout');
  }

  /* Check connector for login status, once user clicks on login.
   * Poll connector to check status every 5secs
   * Timeout connection after 1min
   */
  checkConnectorStatus(connectorUuid: string, type: 'login' | 'logout' = 'login'): void {
    this.myAppsStore.checkConnectorStatus(connectorUuid, type);
  }

  /**
   * Toggles all connectors to be enabled or disabled.
   */
  toggleAllConnectors(state: 'disable' | 'enable'): void {
    this.myAppsStore.toggleAllConnectors(state);
  }

  public showSearch(): void {
    this.searchVisible = true;
  }

  public hideSearch(): void {
    this.searchVisible = false;
  }

  get selectedFilter(): string {
    return this.connectorFilterMenu.find((filter) => filter.checked)?.label ?? '';
  }

  getSelectedFilterCount(option: string): number {
    return this.connectorFilterMenu.find((filter) => filter.name === option)?.count ?? 0;
  }

  ngOnDestroy(): void {
    this.loading = false;
  }
}
